import { useEffect, useState } from 'react';
import { apiFetch, useAuthStore } from '../stores/authStore';
import { startVisibilityAwareInterval } from '../lib/visibilityThrottle';
import { onActiveChange, subscribePush, topicActive } from '../services/relayPush';

/**
 * Shared subscription to GET /api/trader/headroom. Multiple consumers
 * (LimitsWidget, order-form inline preview, etc.) share a single polling
 * loop + cached value so we don't fire the request once per mounted view.
 *
 * Refresh cadence: 7s. Bumps via `bumpHeadroom()` after the caller knows
 * something changed (e.g. just placed/cancelled an order) so we don't have
 * to wait for the poll.
 */

const POLL_MS = 7_000;

export interface HeadroomSnapshot {
  maxContracts: number | null;
  maxPositions: number | null;
  openContracts: number;
  openPositions: number;
  intentContracts: number;
  /** Summary of which trader_rules dimensions are active. Only present
   *  when the relay returns a v2 headroom payload (post sport-rules deploy);
   *  pre-deploy clients see undefined and treat as "no rules active". */
  rulesActive?: {
    sportRestricted: boolean;
    nonSportsBlocked: boolean;
    polymarketBlocked: boolean;
    /** Optional: older relays omit it. */
    polymarketUsBlocked?: boolean;
  };
}

let cached: HeadroomSnapshot | null = null;
let stopPollTimer: (() => void) | null = null;
// Bumped on logout reset — discards an in-flight fetch that began as the
// previous user so it can't repopulate `cached` (their caps/open counts) after
// the wipe and leak across a logout→login in the same tab. Headroom drives the
// LimitsWidget + order-form cap preview (a money surface), so unlike the
// rate-limit sibling this guard is load-bearing, not just hygiene. Mirrors
// useRateLimitStatus / useUserKalshiPOS.
let epoch = 0;
const listeners = new Set<(s: HeadroomSnapshot | null) => void>();
// Whether the latest read failed with nothing to show — kept apart from "not
// read yet" so the Limits strip can say which (#1041). Display only.
let failed = false;
// #1113: wall-clock time of the last good read (poll or push), for the strip's
// "as of" while later reads fail. Display only; null until a read lands.
let lastGoodAtMs: number | null = null;
const failedListeners = new Set<(failed: boolean) => void>();
function setFailed(next: boolean): void {
  if (failed === next) return;
  failed = next;
  failedListeners.forEach((l) => l(failed));
}

async function fetchOnce(): Promise<void> {
  // Snapshot the epoch before the await; if a logout/reset bumps it while the
  // request is in flight, discard the result instead of writing it back.
  const myEpoch = epoch;
  try {
    const res = await apiFetch('/api/trader/headroom');
    if (myEpoch !== epoch) return;
    if (!res.ok) { setFailed(true); return; }
    const next = (await res.json()) as HeadroomSnapshot;
    if (myEpoch !== epoch) return;
    cached = next;
    lastGoodAtMs = Date.now();
    setFailed(false);
    listeners.forEach((l) => l(cached));
  } catch {
    if (myEpoch === epoch) setFailed(true);
  }
}

function ensurePolling(): void {
  if (stopPollTimer) return;
  // Push-fed when the relay stream is up — the timer only arms as fallback.
  if (topicActive('headroom')) return;
  // Hidden tabs poll headroom at 1/6 rate; immediate refresh on refocus.
  stopPollTimer = startVisibilityAwareInterval(() => void fetchOnce(), POLL_MS);
}

function stopPolling(): void {
  if (stopPollTimer) stopPollTimer();
  stopPollTimer = null;
}

// Stream-fed headroom: frames carry the exact /api/trader/headroom payload.
// Poll timers re-arm the moment push drops (and stand down when it returns).
subscribePush('headroom', (payload) => {
  cached = payload as HeadroomSnapshot;
  lastGoodAtMs = Date.now();
  setFailed(false);
  listeners.forEach((l) => l(cached));
});
onActiveChange(() => {
  if (listeners.size === 0) return;
  if (topicActive('headroom')) stopPolling();
  else ensurePolling();
});

/** Force a fresh fetch (e.g. right after placing an order). */
export function bumpHeadroom(): void {
  fetchOnce();
}

/** Wipe the cache — used on logout so a different user doesn't see stale. */
export function resetHeadroomCache(): void {
  epoch += 1; // invalidate any in-flight fetch from the previous user.
  cached = null;
  lastGoodAtMs = null;
  setFailed(false);
  // Still-mounted subscribers (a sibling tab replaced the identity under a
  // live shell) drop the old value and keep their loop for the new identity;
  // with none left (logout unmounted them) the loop stops.
  listeners.forEach((l) => l(null));
  if (listeners.size === 0 || useAuthStore.getState().status !== 'authenticated') stopPolling();
  else void fetchOnce();
}

export function useHeadroom(): HeadroomSnapshot | null {
  const isAuthed = useAuthStore((s) => s.status === 'authenticated');
  const [snap, setSnap] = useState<HeadroomSnapshot | null>(cached);

  useEffect(() => {
    if (!isAuthed) return;
    listeners.add(setSnap);
    // First subscriber kicks off the loop + an immediate fetch.
    if (listeners.size === 1) {
      ensurePolling();
      fetchOnce();
    } else if (cached) {
      // Late subscribers get the cached value immediately so they aren't
      // null-flickering before the next poll lands.
      setSnap(cached);
    }
    return () => {
      listeners.delete(setSnap);
      if (listeners.size === 0) stopPolling();
    };
  }, [isAuthed]);

  return snap;
}

/** When the last good headroom read landed (poll or push), or null. Read during
 *  a render the failed flag or a new snapshot triggered (#1113). */
export function headroomLastGoodAtMs(): number | null {
  return lastGoodAtMs;
}

/** True while the latest shared headroom read failed. With nothing cached the
 *  strip says the limits are unavailable; with a snapshot on screen it says the
 *  figures are not refreshing (#1113). */
export function useHeadroomUnavailable(): boolean {
  const [value, setValue] = useState(failed);
  useEffect(() => {
    setValue(failed);
    failedListeners.add(setValue);
    return () => { failedListeners.delete(setValue); };
  }, []);
  return value;
}
